import type {
  CameraFitParams,
  CameraMemento,
  ICameraController,
  IViewPortOrientation,
  OnCameraModifiedCallback
} from '@/engine/app/interfaces';
import * as GeometryHelper from '@/engine/utils/geometryHelper';
import * as THREE from 'three';
import type { CallbackSubscription } from '@/engine/Utilities/CallbackManager';
import { CallbackManager } from '@/engine/Utilities/CallbackManager';

class _CameraWrapper<T extends THREE.Camera> {
  // WARNING: [chebotarev] Do not expose _camera outside!
  private readonly _camera: T;

  private readonly _onModifiedCallback: () => void;

  public constructor(toWrap: T, onModifiedCallback: () => void) {
    this._camera = toWrap;
    //this._camera.rotation.order = 'YXZ';
    if (!(this._camera as unknown as THREE.OrthographicCamera).isOrthographicCamera) {
      throw new Error('Only Orthographic Camera is supported');
    }
    this._onModifiedCallback = onModifiedCallback;
  }

  public modify(modifier: (camera: T) => void, shouldNotify = true): void {
    modifier(this._camera);
    if (shouldNotify) {
      this._onModifiedCallback();
    }
  }

  public notify(): void {
    this._onModifiedCallback();
  }

  public access<U>(accessor: (camera: T) => U): U {
    return accessor(this._camera);
  }
}

export class CameraController implements ICameraController {
  private _canvas: HTMLCanvasElement;

  private readonly _camera: _CameraWrapper<THREE.OrthographicCamera>;

  private readonly _onCameraModifiedListeners: CallbackManager<OnCameraModifiedCallback> = new CallbackManager();

  private readonly _camDistance = 2000;

  private readonly _fitCenterPoint = new THREE.Vector3();

  public constructor(canvas: HTMLCanvasElement) {
    this._canvas = canvas;
    this._camera = new _CameraWrapper(
      new THREE.OrthographicCamera(
        -this._canvas.width / 2,
        this._canvas.width / 2,
        this._canvas.height / 2,
        -this._canvas.height / 2,
        0.1,
        this._camDistance * 2
      ),
      (): void => {
        this._onCameraModifiedListeners.invoke(this);
      }
    );
  }

  public dispose(): void {
    return;
  }

  public updateCanvas(canvas: HTMLCanvasElement): void {
    this._canvas = canvas;
    this._fitCameraToCanvas();
  }

  public getCanvas(): HTMLCanvasElement {
    return this._canvas;
  }

  public onCameraModified(callback: OnCameraModifiedCallback): CallbackSubscription {
    return this._onCameraModifiedListeners.subscribe(callback);
  }

  public accessCamera<T>(accessor: (camera: THREE.OrthographicCamera) => T): T {
    return this._camera.access(accessor);
  }

  public getFitCenterPoint(): THREE.Vector3 {
    return this._fitCenterPoint;
  }

  public getFrameDimensions(): THREE.Vector2 {
    return new THREE.Vector2(this._canvas.width, this._canvas.height);
  }

  public getCameraZoomFactor(): number {
    return this._camera.access((camera) => (camera.right * 2) / this._canvas.width / camera.zoom);
  }

  public getCameraZoom(): number {
    return this._camera.access((camera) => camera.zoom);
  }

  // Begin ICameraInfoApi

  public getCameraPosition(): THREE.Vector3 {
    return this._camera.access((camera) => camera.position);
  }

  public getCameraRotation(): THREE.Euler {
    return this._camera.access((camera) => camera.rotation);
  }

  public getCameraDirection(): THREE.Vector3 {
    return this._camera.access((camera) => camera.getWorldDirection(new THREE.Vector3())); // For orthographic camera only!
  }

  public getCameraTop(): THREE.Vector3 {
    return this._camera.access((camera) => new THREE.Vector3().setFromMatrixColumn(camera.matrix, 1));
  }

  public getCameraQuaternion(): THREE.Quaternion {
    return this._camera.access((camera) => camera.quaternion);
  }

  public getCameraTransformation(): THREE.Matrix4 {
    return this._camera.access((camera) => camera.matrix);
  }

  public getCameraClone(): THREE.OrthographicCamera {
    return this._camera.access((camera) => camera.clone());
  }

  // End ICameraInfoApi

  // Begin ICameraManipulationApi

  public transformCamera(mtx: THREE.Matrix4, shouldNotify = true): void {
    this._camera.modify((camera) => {
      camera.position.applyMatrix4(mtx);
      camera.quaternion.premultiply(new THREE.Quaternion().setFromRotationMatrix(mtx));
    }, shouldNotify ?? true);
  }

  public rotateCamera(position: THREE.Vector3, direction: THREE.Vector3, angleRad: number, shouldNotify = true): void {
    const mtx = GeometryHelper.makeRotationMatrix(position, direction, angleRad);
    this.transformCamera(mtx, shouldNotify ?? true);
  }

  public panCamera(vector: THREE.Vector3, shouldNotify = true): void {
    const mtx = new THREE.Matrix4().makeTranslation(vector.x, vector.y, vector.z);
    this.transformCamera(mtx, shouldNotify ?? true);
  }

  public zoomCamera(factor: number, ptOfInterest3d?: THREE.Vector3, shouldNotify?: boolean): void {
    this._camera.modify((camera) => {
      const prevZoom = camera.zoom;
      camera.zoom = Math.min(Math.max(camera.zoom * factor, 0.1), 100);
      camera.updateProjectionMatrix();
      if (ptOfInterest3d) {
        const zoomFactor = camera.zoom / prevZoom;
        const center3d = this.unproject(this.project(camera.position));
        camera.position.add(ptOfInterest3d.sub(center3d).multiplyScalar(zoomFactor - 1));
      }
    }, shouldNotify ?? true);
  }

  public notifyCameraChanged(): void {
    this._camera.notify();
  }

  // End ICameraManipulationApi

  // Begin IProjectorApi

  private _toFrame(point: THREE.Vector3): THREE.Vector2 {
    return new THREE.Vector2(((point.x + 1) / 2) * this._canvas.width, (-(point.y - 1) / 2) * this._canvas.height);
  }

  private _fromFrame(point: THREE.Vector2): THREE.Vector3 {
    return new THREE.Vector3(point.x * (2 / this._canvas.width) - 1, -point.y * (2 / this._canvas.height) + 1, -1);
  }

  public project(threeDPoint: THREE.Vector3): THREE.Vector2 {
    return this._toFrame(this._camera.access((camera) => threeDPoint.clone().project(camera)));
  }

  public unproject(screenPoint: THREE.Vector2): THREE.Vector3 {
    return this._camera.access((camera) => this._fromFrame(screenPoint).unproject(camera));
  }

  public getProjectionRay(threeDPoint: THREE.Vector3): THREE.Ray {
    const origin = this._camera.access((camera) => threeDPoint.clone().project(camera));
    const direction = origin.clone().sub(threeDPoint).normalize();
    return new THREE.Ray(origin, direction);
  }

  public getViewRay(screenPoint: THREE.Vector2): THREE.Ray {
    const origin = this.unproject(screenPoint);
    //const direction = new THREE.Vector3().subVectors(origin, this._camera.position); // For perspective camera only!
    const direction = this._camera.access((camera) => camera.getWorldDirection(new THREE.Vector3())); // For orthographic camera only!
    return new THREE.Ray(origin, direction);
  }

  private _getFrameBox(frameIndentFactor?: number): THREE.Box2 {
    const minCorner = new THREE.Vector2(0, 0);
    const maxCorner = this.getFrameDimensions();
    if (frameIndentFactor !== undefined) {
      const indent = maxCorner.clone().multiplyScalar(frameIndentFactor);
      minCorner.add(indent);
      maxCorner.sub(indent);
    }
    return new THREE.Box2(minCorner, maxCorner);
  }

  // End IProjectorApi

  public clampPointToFrame(pt: THREE.Vector3, frameIndentFactor?: number): THREE.Vector3 | undefined {
    const frameBox = this._getFrameBox(frameIndentFactor);
    const pos = this.project(pt);
    if (!frameBox.containsPoint(pos)) {
      const clampedPos = frameBox.clampPoint(pos, new THREE.Vector2());
      return this.unproject(clampedPos).sub(this.unproject(pos).sub(pt));
    } else {
      return undefined;
    }
  }

  public setCameraOrientation(orientation: IViewPortOrientation): void {
    const cameraPosition = this._fitCenterPoint
      .clone()
      .addScaledVector(orientation.directionVector.clone().normalize(), this._camDistance);

    this._camera.modify((camera) => {
      camera.position.copy(cameraPosition);
      camera.up = orientation.topVector.clone().normalize();
      camera.lookAt(this._fitCenterPoint);
    });
  }

  public fitCamera(target: THREE.Vector3, width: number, height: number, fitParams?: CameraFitParams): void {
    if (width <= 0 || height <= 0) {
      return;
    }

    this._camera.modify((camera) => {
      const cameraPosition = target
        .clone()
        .add(camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(-this._camDistance));
      camera.position.copy(cameraPosition);
      camera.lookAt(target);
    }, false);

    this._fitCenterPoint.copy(target);

    const widthFittingFactor = fitParams?.widthFittingFactor ?? fitParams?.fittingFactor ?? 1.0;
    const heightFittingFactor = fitParams?.heightFittingFactor ?? fitParams?.fittingFactor ?? 1.0;

    this._fitCameraToCanvas({
      viewWidth: width / widthFittingFactor,
      viewHeight: height / heightFittingFactor,
      fitSideOverride: fitParams?.fitSideOverride
    });
  }

  public fitCameraToObjects(fitObjects: Array<THREE.Mesh>, fitParams?: CameraFitParams): void {
    const params = GeometryHelper.getFittedCameraToObjectsParams(
      this._camera.access((camera) => camera.matrixWorld),
      fitObjects
    );

    this.fitCamera(params.cameraTarget, params.viewWidth, params.viewHeight, fitParams);
  }

  public fitCameraToPoints(points: Array<THREE.Vector3>, fitParams?: CameraFitParams): void {
    const cameraMatrixWorld = this._camera.access((camera) => camera.matrixWorld);
    const bBox = new THREE.Box3();
    for (const pt of points) {
      bBox.expandByPoint(pt.clone().applyMatrix4(cameraMatrixWorld.clone().invert()));
    }
    const params = GeometryHelper.getFittedCameraToBBoxParams(cameraMatrixWorld, bBox);

    this.fitCamera(params.cameraTarget, params.viewWidth, params.viewHeight, fitParams);
  }

  private _fitCameraToCanvas(params?: {
    viewWidth: number;
    viewHeight: number;
    fitSideOverride?: 'width' | 'height';
  }): void {
    this._camera.modify((camera) => {
      const dstRatio = this._canvas.width / this._canvas.height;
      if (params) {
        // View params are provided as an input - fit it inside the canvas
        const srcRatio = params.viewWidth / params.viewHeight;
        const fitHorizontalSize =
          params.fitSideOverride === undefined ? srcRatio > dstRatio : params.fitSideOverride === 'width';
        const viewSize = fitHorizontalSize ? params.viewWidth : params.viewHeight;
        const delta = viewSize / 2;
        if (fitHorizontalSize) {
          camera.top = delta / dstRatio;
          camera.bottom = -delta / dstRatio;
          camera.left = -delta;
          camera.right = delta;
        } else {
          camera.left = -delta * dstRatio;
          camera.right = delta * dstRatio;
          camera.top = delta;
          camera.bottom = -delta;
        }
        camera.zoom = 1;
      } else {
        // View params are NOT provided - fit the camera to the canvas
        const preserveHorizontalSize = false; // [chebotarev] Be careful changing this to true, affects both Cardiac and Thoracis
        if (preserveHorizontalSize) {
          // preserve horizontal size
          const xSize = camera.right - camera.left;
          const yCenter = (camera.bottom + camera.top) / 2;
          camera.top = yCenter + xSize / dstRatio / 2;
          camera.bottom = yCenter - xSize / dstRatio / 2;
        } else {
          // preserve vertical size
          const ySize = camera.top - camera.bottom;
          const xCenter = (camera.left + camera.right) / 2;
          camera.right = xCenter + (ySize * dstRatio) / 2;
          camera.left = xCenter - (ySize * dstRatio) / 2;
        }
      }
      camera.updateProjectionMatrix();
    });
  }

  public handleResize(): void {
    this._fitCameraToCanvas();
  }

  public synchronizeWith(another: ICameraController): void {
    this._camera.modify((dstCamera) => {
      const dstRatio = this._canvas.width / this._canvas.height;
      another.accessCamera((srcCamera) => {
        // sync location and orientation
        dstCamera.position.copy(srcCamera.position);
        dstCamera.quaternion.copy(srcCamera.quaternion);
        // sync horizontal size
        const yCenter = (srcCamera.bottom + srcCamera.top) / 2;
        const xSize = srcCamera.right - srcCamera.left;
        dstCamera.left = srcCamera.left;
        dstCamera.right = srcCamera.right;
        dstCamera.top = yCenter + xSize / dstRatio / 2;
        dstCamera.bottom = yCenter - xSize / dstRatio / 2;
        // sync zoom
        dstCamera.zoom = srcCamera.zoom;
        // update projection matrix on size and zoom update
        dstCamera.updateProjectionMatrix();
      });
    });
  }

  public getMemento(): CameraMemento {
    return this._camera.access((srcCamera) => ({
      position: srcCamera.position.toArray(),
      quaternion: srcCamera.quaternion.toArray(),
      left: srcCamera.left,
      right: srcCamera.right,
      yCenter: (srcCamera.bottom + srcCamera.top) / 2,
      zoom: srcCamera.zoom
    }));
  }

  public setMemento(memento: CameraMemento): void {
    this._camera.modify((dstCamera) => {
      const dstRatio = this._canvas.width / this._canvas.height;
      // sync location and orientation
      dstCamera.position.fromArray(memento.position);
      dstCamera.quaternion.fromArray(memento.quaternion as unknown as THREE.QuaternionTuple);
      // sync horizontal size
      const xSize = memento.right - memento.left;
      dstCamera.left = memento.left;
      dstCamera.right = memento.right;
      dstCamera.top = memento.yCenter + xSize / dstRatio / 2;
      dstCamera.bottom = memento.yCenter - xSize / dstRatio / 2;
      // sync zoom
      dstCamera.zoom = memento.zoom;
      // update projection matrix on size and zoom update
      dstCamera.updateProjectionMatrix();
    });
  }

  private _getPtsBBox(pts: Array<THREE.Vector3>): THREE.Box2 {
    if (pts.length < 1) {
      throw new Error('Invalid input');
    }
    const bBox = new THREE.Box2();
    for (const pt of pts) {
      bBox.expandByPoint(this.project(pt));
    }
    return bBox;
  }

  public centrifyCamera(pt: THREE.Vector3, shouldResetZoom: boolean): void {
    this._camera.modify((camera) => {
      const origin = new THREE.Vector3(0, 0, 0).unproject(camera);
      const location = pt.clone().project(camera).setComponent(2, 0).unproject(camera);
      camera.position.add(location.sub(origin));
      if (shouldResetZoom === true) {
        camera.zoom = 1;
        camera.updateProjectionMatrix();
      }
    });
  }

  public ensurePointIsVisible(pt: THREE.Vector3, frameIndentFactor: number): void {
    this._camera.modify((camera) => {
      const clampedPt = this.clampPointToFrame(pt, frameIndentFactor);
      if (clampedPt) {
        camera.position.add(pt.clone().sub(clampedPt));
      }
    });
  }

  public ensurePointsAreVisible(pts: Array<THREE.Vector3>, frameIndentFactor: number): void {
    let bBox = this._getPtsBBox(pts);
    const frameBox = this._getFrameBox(frameIndentFactor);
    if (!frameBox.containsBox(bBox)) {
      const frameSize = frameBox.getSize(new THREE.Vector2());
      const bBoxSize = bBox.getSize(new THREE.Vector2());
      if (frameSize.x < bBoxSize.x || frameSize.y < bBoxSize.y) {
        // requires unZoom
        const factor = Math.max(bBoxSize.x / frameSize.x, bBoxSize.y / frameSize.y);
        this._camera.modify((camera) => {
          camera.zoom /= factor;
          camera.updateProjectionMatrix();
        });
        // update BBox
        bBox = this._getPtsBBox(pts);
        bBox.getSize(bBoxSize);
      }
      const dX = bBoxSize.x / 2;
      const dY = bBoxSize.y / 2;
      const vec = new THREE.Vector2();
      const bBoxCenter = bBox.getCenter(new THREE.Vector2());
      // get the BBox corner points
      const p1 = bBoxCenter.clone().add(vec.set(-dX, -dY));
      const p2 = bBoxCenter.clone().add(vec.set(dX, -dY));
      const p3 = bBoxCenter.clone().add(vec.set(dX, dY));
      const p4 = bBoxCenter.clone().add(vec.set(-dX, dY));
      // clamp the corner points to frame
      const c1 = frameBox.clampPoint(p1, new THREE.Vector2());
      const c2 = frameBox.clampPoint(p2, new THREE.Vector2());
      const c3 = frameBox.clampPoint(p3, new THREE.Vector2());
      const c4 = frameBox.clampPoint(p4, new THREE.Vector2());
      // find clamping distances
      const l1 = p1.distanceToSquared(c1);
      const l2 = p2.distanceToSquared(c2);
      const l3 = p3.distanceToSquared(c3);
      const l4 = p4.distanceToSquared(c4);
      const transl = new THREE.Vector3();
      // find the largest clamping distance in 3D space
      if (l1 >= l2 && l1 >= l3 && l1 >= l4) {
        transl.subVectors(this.unproject(c1), this.unproject(p1));
      } else if (l2 >= l1 && l2 >= l3 && l2 >= l4) {
        transl.subVectors(this.unproject(c2), this.unproject(p2));
      } else if (l3 >= l1 && l3 >= l2 && l3 >= l4) {
        transl.subVectors(this.unproject(c3), this.unproject(p3));
      } else {
        transl.subVectors(this.unproject(c4), this.unproject(p4));
      }
      this._camera.modify((camera) => {
        camera.position.sub(transl);
      });
    }
  }

  public resetZoom(): void {
    this._camera.modify((camera) => {
      camera.zoom = 1;
      camera.updateProjectionMatrix();
    });
  }
}
